import React, { useMemo } from 'react';
import { Droppable, type DroppableProvided } from 'react-beautiful-dnd';
import { FormattedMessage, useIntl } from 'react-intl';
import { faCircleCheck } from '@fortawesome/pro-light-svg-icons/faCircleCheck';
import { faGripVertical } from '@fortawesome/pro-solid-svg-icons/faGripVertical';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AlertBanner } from '@shipstation/client-common/components/alert-banner';

import { FlexColumn } from 'ui-components/components/flex/column';
import { Gap } from 'ui-components/components/flex/gap';
import { Text, TextType } from 'ui-components/components/text';

import { PackingRecommend } from '../../../../../orders/order-details/configure-shipment/fields/packing-recommend/packing-recommend';
import { UNASSIGNED_ITEMS_ID } from '../../constants/drag-drop';
import { useMultiPackageModalContext } from '../../contexts/multi-package-modal-context';
import { getMaxQuantityForChunk, getUnassignedItems, hasZeroQuantityItem } from '../../helpers/assignment-helpers';
import { ItemCard } from '../item-card/item-card';
import { messages } from './order-items-panel-messages';
import style from './order-items-panel.module.css';

export const OrderItemsPanel = () => {
  const { formatMessage } = useIntl();
  const { fulfillmentPlanId, packages, items, itemQuantities, updateChunkQuantity } = useMultiPackageModalContext();
  const unassignedItems = useMemo(() => getUnassignedItems(items, itemQuantities), [items, itemQuantities]);

  const allItemsAssigned = unassignedItems.length === 0;
  const isAllocationUnavailable = useMemo(() => hasZeroQuantityItem(items), [items]);

  return (
    <FlexColumn gap={Gap.Space16} className={style['order-items-panel']}>
      <FlexColumn gap={Gap.Space4}>
        <Text textType={TextType.H3} value={messages.orderItemsTitle} />
        <Text textType={TextType.Paragraph} value={messages.orderItemsInstruction} />
        <Text textType={TextType.Caption} value={messages.orderItemsCaption} className={style['order-item-caption']} />
      </FlexColumn>

      <AlertBanner
        status='inform'
        title={formatMessage(isAllocationUnavailable ? messages.allocationUnavailableHeading : messages.allocationOptionalHeading)}
        description={
          <FormattedMessage {...(isAllocationUnavailable ? messages.allocationUnavailableMessage : messages.allocationOptionalMessage)} />
        }
      />

      <PackingRecommend currentPackages={packages} fulfillmentPlanId={fulfillmentPlanId} />

      <Droppable droppableId={UNASSIGNED_ITEMS_ID}>
        {(provided: DroppableProvided) => (
          <FlexColumn ref={provided.innerRef} {...provided.droppableProps} className={style['items-list']} gap={Gap.Space16}>
            {allItemsAssigned ? (
              <FlexColumn gap={Gap.Space16} className={style['order-item-empty-state']}>
                <FontAwesomeIcon icon={faCircleCheck} className={style.checkmark} />
                <Text textType={TextType.Label} value={messages.allItemsAssigned} />
              </FlexColumn>
            ) : (
              <>
                {unassignedItems.map((chunk, index) => (
                  <ItemCard
                    key={chunk.chunkId}
                    item={chunk.item}
                    chunkId={chunk.chunkId}
                    index={index}
                    isDragDisabled={isAllocationUnavailable}
                    quantity={chunk.quantity}
                    maxQuantity={getMaxQuantityForChunk(chunk.item.fulfillmentPlanItemId, chunk.chunkId, itemQuantities)}
                    onQuantityChange={updateChunkQuantity}
                    leftAction={<FontAwesomeIcon icon={faGripVertical} />}
                  />
                ))}
              </>
            )}
            {provided.placeholder}
          </FlexColumn>
        )}
      </Droppable>
    </FlexColumn>
  );
};
